/* eslint-disable */
import React, { useEffect, useState, useMemo } from 'react';
import { Button, Box, Grid, Typography, Divider } from '@mui/material';
import { api } from '../requests/axios';

const needleOptions = [
  { straight: 'Straight' },
  { dbpt: 'Double Pointed' },
  { cir_16: 'Circular 16' },
  { cir_24: 'Circular 24' },
  { cir_32: 'Circular 32' }
];

interface KnittingNeedle {
  id: number;
  needle_type: 'straight' | 'dbpt' | 'cir_16' | 'cir_24' | 'cir_32';
  us_size: number;
  mm_size: number;
}

interface KnittingToolsChartProps {
  onSaveRef?: React.MutableRefObject<(() => Promise<{ added: number[]; removed: number[] }>) | null>;
  showSaveButton?: boolean;
}

export default function KnittingToolsChart({ onSaveRef, showSaveButton = true }: KnittingToolsChartProps) {
  const [usersKnittingNeedles, setUsersKnittingNeedles] = useState<KnittingNeedle[]>([]);
  const [knittingNeedlesData, setKnittingNeedlesData] = useState<KnittingNeedle[]>([]);
  const [usSizes, setUsSizes] = useState<number[]>([]);
  const [mmSizes, setMmSizes] = useState<number[]>([]);
  const [selectedNeedleIds, setSelectedNeedleIds] = useState(new Set());
  const [initialNeedleIds, setInitialNeedleIds] = useState(new Set());

  const setData = async () => {
    const usersNeedlesResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/users/knitting_needles.json`);
    const userData = await usersNeedlesResponse.data;

    const allNeedlesResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/knitting_needles.json`);
    const allNeedleData = await allNeedlesResponse.data;

    setUsersKnittingNeedles(userData.knitting_needles);
    setKnittingNeedlesData(allNeedleData.knitting_needles);
    setUsSizes(allNeedleData.us_sizes);
    setMmSizes(allNeedleData.mm_sizes);
  };

  useEffect(() => {
    setData();
  }, []);

  useEffect(() => {
    const ids = new Set(usersKnittingNeedles.map(n => n.id));
    setSelectedNeedleIds(ids);
    setInitialNeedleIds(ids);
  }, [usersKnittingNeedles]);

  // Create a lookup map for O(1) needle access: Map<needle_type, Map<us_size, needle>>
  const needleLookupMap = useMemo(() => {
    const map = new Map<string, Map<number, KnittingNeedle>>();
    knittingNeedlesData.forEach(needle => {
      if (!map.has(needle.needle_type)) {
        map.set(needle.needle_type, new Map());
      }
      map.get(needle.needle_type)!.set(needle.us_size, needle);
    });
    return map;
  }, [knittingNeedlesData]);

  const handleBoxChange = (event: React.MouseEvent<HTMLDivElement>) => {
    const id = parseInt((event.target as HTMLDivElement).id, 10);
    setSelectedNeedleIds(prev => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  const getBackground = (id: number) => {
    return selectedNeedleIds.has(id) ? '#5A7D6B' : '#e0e0e0';
  };

  const getSaveData = () => {
    const added = Array.from(selectedNeedleIds).filter(id => !initialNeedleIds.has(id)) as number[];
    const removed = Array.from(initialNeedleIds).filter(id => !selectedNeedleIds.has(id)) as number[];
    return { added, removed };
  };

  const handleSave = async () => {
    const { added, removed } = getSaveData();

    try {
      await api.post('/users/bulk_update_needles.json', { added, removed });
      setInitialNeedleIds(new Set(selectedNeedleIds));
      alert('Changes saved!');
    } catch (error) {
      console.error('Save failed:', error);
      alert('Error saving changes');
    }
  };

  // Expose save function via ref
  React.useEffect(() => {
    if (onSaveRef) {
      onSaveRef.current = async () => {
        const { added, removed } = getSaveData();
        await api.post('/users/bulk_update_needles.json', { added, removed });
        setInitialNeedleIds(new Set(selectedNeedleIds));
        return { added, removed };
      };
    }
  }, [selectedNeedleIds, initialNeedleIds, onSaveRef]);

  return (
    <>
      <Box sx={{ 
        p: { xs: 1, sm: 2 },
        overflowX: 'auto',
        width: '100%'
      }}>
        {/* Header Row */}
        <Box sx={{ minWidth: { xs: '600px', sm: 'auto' } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Knitting Needles
          </Typography>
          
          <Grid container alignItems="center" spacing={0.5}>
            <Grid item sx={{ width: { xs: 100, sm: 120 }, flexShrink: 0, pr: { xs: 1, sm: 2 } }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  textAlign: 'right'
                }}
              >
                US
              </Typography>
            </Grid>
            {usSizes.map((us: number, idx: number) => {
              return (
                <Grid item key={`us-${idx}`} sx={{ 
                  minWidth: { xs: 24, sm: 30 },
                  width: { xs: 24, sm: 30 },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{us}</Typography>
                </Grid>
              );
            })}
          </Grid>
          <Grid container alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <Grid item sx={{ width: { xs: 100, sm: 120 }, flexShrink: 0, pr: { xs: 1, sm: 2 } }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  textAlign: 'right'
                }}
              >
                Metric
              </Typography>
            </Grid>
            {mmSizes.map((mm: number, idx: number) => (
              <Grid item key={`mm-${idx}`} sx={{ 
                minWidth: { xs: 24, sm: 30 },
                width: { xs: 24, sm: 30 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{mm}</Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 0.75 }} />

          {/* Rows for Each Needle Type */}
          {needleOptions.map(i => {
            const key = Object.keys(i)[0];
            const label = Object.values(i)[0];
            return (
              <Grid container alignItems="center" spacing={0.5} key={key} sx={{ mt: 1 }}>
                <Grid item sx={{ width: { xs: 100, sm: 120 }, flexShrink: 0, pr: { xs: 1, sm: 2 } }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      textAlign: 'right'
                    }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {usSizes.map((us: number, idx: number) => {
                  const needle = needleLookupMap.get(key)?.get(us);
                  return (
                    <Grid item key={`${key}-${us}-${idx}`} sx={{ 
                      minWidth: { xs: 24, sm: 30 },
                      width: { xs: 24, sm: 30 },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0
                    }}>
                      {needle ? (
                        <Box
                          id={needle.id.toString()}
                          sx={{
                            height: { xs: 16, sm: 20 },
                            width: { xs: 16, sm: 20 },
                            bgcolor: getBackground(needle.id),
                            borderRadius: 0.5,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }
                          }}
                          onClick={handleBoxChange}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: { xs: 16, sm: 20 },
                            width: { xs: 16, sm: 20 }
                          }}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            );
          })}
        </Box>
      </Box>
      {showSaveButton && (
        <Button 
          variant="contained" 
          onClick={handleSave} 
          sx={{ 
            m: { xs: 1, sm: 2 },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Save Changes
        </Button>
      )}
    </>
  );
}
